/** Assistant settings saved with an account theme. */
export interface AssistantSettings {
  name: string;
  greeting: string;
  introMode: 'standard' | 'custom';
  intro: string;
}

/** Current public translations for authored assistant copy, keyed by locale. */
export type AssistantTranslations = Record<string, AssistantLocaleTranslation>;

export interface AssistantLocaleTranslation {
  greeting?: string;
  intro?: string;
}

export type AssistantField = 'greeting' | 'intro';

export const DEFAULT_ASSISTANT_SETTINGS: AssistantSettings = {
  name: 'Ema',
  greeting: 'Hi there!',
  introMode: 'standard',
  intro: "I'm Ema — your friendly EmBot assistant. What can I help you with today?",
};

/** Build the standard intro while retaining the assistant name literally. */
export function getStandardAssistantIntro(name: string): string {
  return `I'm ${name} — your friendly EmBot assistant. What can I help you with today?`;
}

/** Normalize old, absent, or malformed theme data to complete settings. */
export function normalizeAssistantSettings(value: unknown): AssistantSettings {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return { ...DEFAULT_ASSISTANT_SETTINGS };
  }

  const raw = value as Record<string, unknown>;
  const name = typeof raw['name'] === 'string' && raw['name'].trim() !== ''
    ? raw['name']
    : DEFAULT_ASSISTANT_SETTINGS.name;
  const greeting = typeof raw['greeting'] === 'string'
    ? raw['greeting']
    : DEFAULT_ASSISTANT_SETTINGS.greeting;
  const introMode = raw['introMode'] === 'custom' ? 'custom' : 'standard';
  const intro = introMode === 'standard'
    ? getStandardAssistantIntro(name)
    : typeof raw['intro'] === 'string'
      ? raw['intro']
      : '';

  return { name, greeting, introMode, intro };
}

/**
 * Resolve a complete Assistant object by theme-row ownership. A missing row
 * inherits its base row; a present row with missing/null settings uses defaults.
 */
export function resolveAssistantSettingsForTheme(
  themeRow: { assistant?: AssistantSettings | null } | null | undefined,
  inheritedThemeRow?: { assistant?: AssistantSettings | null } | null,
): AssistantSettings {
  const effectiveThemeRow = themeRow ?? inheritedThemeRow;
  return normalizeAssistantSettings(effectiveThemeRow?.assistant);
}

/**
 * Resolve one learner-facing field. Empty strings are intentional values and
 * therefore never trigger a fallback.
 */
export function resolveAssistantFieldText(
  settings: AssistantSettings,
  translations: AssistantTranslations | null | undefined,
  locale: string,
  field: AssistantField,
  localizedStandardText?: string,
): string {
  const source = field === 'greeting'
    ? settings.greeting
    : settings.introMode === 'standard'
      ? getStandardAssistantIntro(settings.name)
      : settings.intro;
  const isStandard = field === 'greeting'
    ? source === DEFAULT_ASSISTANT_SETTINGS.greeting
    : settings.introMode === 'standard';

  if (source === '') return '';
  if (isStandard) return localizedStandardText ?? source;

  const normalizedLocale = locale.toLowerCase().replace('_', '-');
  const localized = translations?.[normalizedLocale]?.[field]
    ?? translations?.[normalizedLocale.split('-')[0] ?? '']?.[field];
  return localized ?? source;
}
